<template>
  <div>
    <div v-if="loading" class="loader">
      <tml-loader :loading="loading" height="100vh" />
    </div>

    <div class="paypal-checkout relative z-0">
      <div ref="paypal" class="flex" />
    </div>

    <tml-new-popup
      name="paypal-confirm-shipping-popup"
      layout="standard"
      size="normal"
      height="auto"
      title="Confirm order"
      @leave="onClosed"
    >
      <template #content>
        <cart-confirm-shipping
          :checkout-resource="checkoutResource"
          :show-tax="showTax"
          :name="name"
          :pre-selected-shipping="selectedShippingMethod"
          @unseen-messages="onUnseenMessages"
        />
      </template>
    </tml-new-popup>
  </div>
</template>

<script>
import {dayjs, http, snackbar} from '@teemill/common/services';
import {tracker} from '@teemill/common/plugins';

import {formatUrl, getShippingEstimate} from '@teemill/common/helpers';
import CartConfirmShipping from './CartConfirmShipping.vue';

import {captureException, configureScope} from '@sentry/vue';
import {usePaymentProcessorStore} from '@teemill/common/vuex';

export default {
  name: 'CartPaypalButton',

  components: {
    CartConfirmShipping,
  },

  inject: ['app', 'theme'],

  provide() {
    return {
      confirm: this.confirm,
    };
  },

  props: {
    total: {
      type: Number,
      required: true,
    },

    shipping: {
      type: Number,
      default: 0,
    },

    discount: {
      type: Number,
      default: 0,
    },

    showTax: {
      type: Boolean,
      default: true,
    },

    name: {
      type: String,
      required: true,
    },

    routePrefix: {
      type: String,
      required: true,
    },

    mailOptIn: {
      type: Boolean,
      default: false,
    },

    items: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: false,
      selectedShippingMethodCode: null,

      paypalOrderId: false,
      checkoutResource: {},

      buttonColor: 'silver',
      actions: null,

      previousPayload: null, // For debugging Order patch issues
    };
  },

  computed: {
    mappedShippingMethods() {
      return this.checkoutResource.shippingMethods?.map((method, index) => ({
        id: method.code,
        label: this.getShippingMethodLabel(method),
        type: 'SHIPPING',
        selected: method.code === this.selectedShippingMethod.code,
        amount: {
          value: (Math.round(method.price * 100) / 100).toFixed(2),
          currency_code: method.currency ?? 'GBP',
        },
      }));
    },

    selectedShippingMethod() {
      const selected = this.checkoutResource.shippingMethods?.find(
        method => method.code === this.selectedShippingMethodCode
      );

      return selected || this.checkoutResource.shippingMethods?.[0];
    },

    isRapanui: () => $store.state.subdomain?.division === 14,

    currencyCode() {
      return this.$store?.state?.subdomain?.currency ?? 'GBP';
    },

    payerId() {
      return usePaymentProcessorStore().getter('paypalPayerId');
    },
  },

  mounted() {
    this.getPaypalClientId();
  },

  beforeUnmount() {
    this.onClosed();
  },

  methods: {
    formatUrl,

    dayjs,

    getPaypalClientId() {
      http
        .get(formatUrl('/omnis/v3/checkout/get-paypal-client-id/'), {
          params: {
            partner_client: !!this.payerId,
          },
        })
        .success(data => {
          this.clientId = data.clientId;
          const script = document.createElement('script');
          let sdkSrc = `https://www.paypal.com/sdk/js?currency=${this.currencyCode}&client-id=${this.clientId}&commit=false`;

          if (this.payerId) {
            sdkSrc += `&merchant-id=${this.payerId}`;
          }

          script.src = sdkSrc;
          script.setAttribute(
            'data-partner-attribution-id',
            import.meta.env.VITE_PAYPAL_PARTNER_ATTRIBUTION_ID
          );

          script.setAttribute('data-popups-disabled', true);

          script.addEventListener('load', this.setLoaded);
          document.body.appendChild(script);
        });
    },

    setLoaded() {
      if (window.paypal?.Buttons) {
        // render the buttons
        const button = window.paypal.Buttons({
          style: {
            layout: 'horizontal',
            color: this.buttonColor,
            height: 48,
            tagline: 'false',
          },
          onClick: () => {
            this.$emit('click');
          },
          // eslint-disable-next-line
          createOrder: (data, actions) => {
            return actions.order.create({
              purchase_units: [
                {
                  amount: {
                    currency_code: this.currencyCode,
                    value: this.total,
                    breakdown: {
                      item_total: {
                        currency_code: this.currencyCode,
                        value: (
                          Math.round(
                            this.items.reduce(
                              (acc, item) =>
                                acc + item.priceIncTax * item.quantity,
                              0
                            ) * 100
                          ) / 100
                        ).toFixed(2),
                      },
                      shipping: {
                        currency_code: this.currencyCode,
                        value: (Math.round(this.shipping * 100) / 100).toFixed(
                          2
                        ),
                      },
                      discount: {
                        currency_code: this.currencyCode,
                        value: (Math.round(this.discount * 100) / 100).toFixed(
                          2
                        ),
                      },
                    },
                  },
                  items: this.items.map(item => ({
                    name: item.name,
                    description: item.name,
                    quantity: item.quantity,
                    unit_amount: {
                      currency_code: this.currencyCode,
                      value: item.priceIncTax,
                    },
                  })),
                },
              ],
              application_context: {
                user_action: 'PAY_NOW',
                shipping_preference: 'GET_FROM_FILE',
              },
            });
          },
          onApprove: async (paypalData, actions) => {
            this.actions = actions;
            this.paypalOrderId = paypalData.orderID;
            this.loading = true;

            await http
              .post(
                formatUrl(
                  `/omnis/v3/checkout/${this.name}/paypal-express-update-checkout-data/`
                ),
                {
                  paypal_order: paypalData.orderID,
                }
              )
              .success(response => {
                this.handleCheckoutResource(response);
              })
              .oops();

            await this.confirm(
              this.checkoutResource.checkoutData,
              this.selectedShippingMethod?.value
            );

            this.loading = false;
          },
          onShippingChange: async (data, actions) => {
            try {
              await this.updateShipping(data.shipping_address);

              this.selectedShippingMethodCode =
                data.selected_shipping_option?.id;

              const shippingPrice = this.selectedShippingMethod?.price || 0;

              const payload = [
                {
                  op: this.selectedShippingMethodCode ? 'replace' : 'add',
                  path: `/purchase_units/@reference_id=='default'/shipping/options`,
                  value: this.mappedShippingMethods,
                },
                {
                  op: 'replace',
                  path: `/purchase_units/@reference_id=='default'/amount`,
                  value: {
                    currency_code: this.currencyCode,
                    value:
                      Math.round(
                        (this.checkoutResource.summary.total + shippingPrice) *
                          100
                      ) / 100,
                    breakdown: {
                      shipping: {
                        currency_code: this.currencyCode,
                        value: (Math.round(shippingPrice * 100) / 100).toFixed(
                          2
                        ),
                      },
                      item_total: {
                        currency_code: this.currencyCode,
                        value: this.checkoutResource.summary.subtotal,
                      },
                      discount: {
                        currency_code: this.currencyCode,
                        value: this.checkoutResource.summary.discount,
                      },
                    },
                  },
                },
              ];

              this.previousPayload = payload;

              await actions.order.patch(payload);
            } catch (err) {
              actions.resolve();

              configureScope(scope => {
                scope.setExtra('payload', this.previousPayload);
              });

              captureException(err);
            }
          },
          onError: err => {
            tracker.event('payment-failed');

            captureException(err);

            snackbar.error(
              'We cannot process your PayPal payment, please try paying by card'
            );
          },
        });

        if (this.$refs.paypal) {
          button.render(this.$refs.paypal).catch(() => {});
        }
      } else {
        // show a fallback experience
      }
    },

    async updateShipping(shippingAddress) {
      this.loading = true;

      try {
        const {data} = await http.post(
          formatUrl(
            `/omnis/v3/checkout/${this.name}/paypal-express-update-shipping-address/`
          ),
          {
            shipping_address: shippingAddress,
          }
        );

        this.handleCheckoutResource(data);

        if (data.hasUnseenMessages) {
          this.onUnseenMessages();
        }
      } catch (err) {
        this.loading = false;

        throw err;
      }

      this.loading = false;
    },

    handleCheckoutResource(data) {
      this.checkoutResource = data;
    },

    onUnseenMessages(
      errorMessage = 'Some of the items in your bag are low or out of stock'
    ) {
      snackbar.error(errorMessage);

      this.$overlays.close('paypal-confirm-shipping-popup');
    },

    async confirm(checkoutData, shippingMethod) {
      return await http
        .post(
          formatUrl(`${this.routePrefix}${this.name}/paypal-express-pay/`),
          {
            billing_info: checkoutData.billing_info,
            shipping_method: shippingMethod,
            paypal_order: this.paypalOrderId,
            mailOptIn: this.mailOptIn,
          }
        )
        .success(data => {
          this.$router.push({path: `/receipt/${data}/`});
          this.$store.commit('cart/clear');
        })
        .validation(message => {
          snackbar.error(message);
          this.$overlays.open('paypal-confirm-shipping-popup');
        })
        .handle(409, error => {
          if (error.code === 'coupon_code_invalid') {
            this.onUnseenMessages(error.message);
            this.$emit('invalid-coupon');
          } else {
            if (this.actions) {
              this.actions.restart();
            }
          }
        })
        .handle(405, () => {
          snackbar.error('Your basket has changed, please try again');

          this.$emit('basket-changed');
        })
        .handle(['4xx', '5xx'], data => {
          snackbar.error(data.message);
          this.$overlays.close('paypal-confirm-shipping-popup');
        });
    },

    getShippingMethodLabel(shippingOption) {
      if (this.theme().get('checkout.shipping.showEstimates') !== '1') {
        return shippingOption.text;
      }

      return `${shippingOption.text} ${getShippingEstimate(
        shippingOption?.earliestShippingEstimate,
        shippingOption?.latestShippingEstimate
      )}`;
    },

    onClosed() {
      this.$emit('closed');
    },
  },
};
</script>

<style lang="scss" scoped>
.loader {
  position: fixed;
  z-index: 1000;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  background-color: rgba(0, 0, 0, 0.25);
}
</style>
